import { FC } from 'react';
import { z } from "zod";
import { isValidIBAN } from 'ibantools';
import SignatureProcess from '@/components/SignatureProcess';
import ErrorMessage from '@/components/Error/ErrorMessage';

import {
  Checkbox, Select,
  FormControlLabel, Radio, RadioGroup, MenuItem,
  FormControl, InputLabel, Grid,
  Typography,
  DialogActions,
  DialogContent,
  FormHelperText,
  TextField
} from '@mui/material';
import MultiUploadButton from '@/components/MultiUploadButton';
import Banner from "@/components/Banner/Banner";
import { useTranslation } from 'react-i18next';
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';
import { useContracts } from '@/components/Contracts/hooks/useContracts';
import { ContractDisplayTypes } from '@/components/Contracts/types/contracts';
import { LoadingAnimation } from '@/components/LoadingAnimation';
import useIBAN from '@/components/Contracts/hooks/useIBAN';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { IBANData } from '@/components/Contracts/types/iban';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@gisce/oficina-virtual-components';

interface ChangeIbanProps {
  type?: ContractDisplayTypes;
  onClose: () => void;
}

const ChangeIban: FC<ChangeIbanProps> = ({
  type = "electricity",
  onClose
}) => {
  const { t } = useTranslation();
  const Settings = useSettingsContext();
  
  const { currentContract } = useContracts(type); // TODO: Passar aquí si volem contractes de llum o gas
  const { state: IBANsState, changeIBAN, ibans, waitingForSignature } = useIBAN({type});

  const schema = z.discriminatedUnion("alreadyRegistered", [
    z.object({
      alreadyRegistered: z.literal('yes'),
      bankId: z.number({message: t('common:text.iban_select_empty')}),
      files: z.object({
        name: z.string(),
        content: z.string().regex(/^data:.*\/[a-zA-Z]+;base64,/, t('common:text.invalid_attachment_type')),
      }).array().refine((value) => {
        if (Settings?.features?.attachmentOnIbanChange) {
          return value.length > 0;
        }
        return true;
      }, {
        message: t('common:text.iban_change_has_files'),
      })
    }),
    z.object({
      alreadyRegistered: z.literal('no'),
      iban: z.string().refine((value) => {
        return isValidIBAN(value);
      }, {
        message: t('common:text.iban_field_invalid'),
      }),
      isOwner: z.boolean({
        message: t('common:text.iban_change_validation'),
      }).refine(val => val, {message: t('common:text.iban_change_validation')}),
      files: z.object({
        name: z.string(),
        content: z.string().regex(/^data:.*\/[a-zA-Z]+;base64,/, t('common:text.invalid_attachment_type')),
      }).array().refine((value) => {
        if (Settings?.features?.attachmentOnIbanChange) {
          return value.length > 0;
        }
        return true;
      }, {
        message: t('common:text.iban_change_has_files'),
      })
    })
  ]) satisfies z.ZodType<IBANData>;

  const {
    handleSubmit,
    watch,
    getValues,
    control,
    formState: { errors }
  } = useForm<IBANData>({
    resolver: zodResolver(schema),
    defaultValues: {
      alreadyRegistered: "yes",
      files: []
    }
  });

  // RHF discriminated unions limitation 
  // https://stackoverflow.com/questions/74219465/typescript-react-hook-form-error-handling-with-zod-union-schema
  const existingIBANErrors: FieldErrors<
    Extract<IBANData, {alreadyRegistered: 'yes'}>
  > = errors;
  const newIBANErrors: FieldErrors<
    Extract<IBANData, {alreadyRegistered: 'no'}>
  > = errors;

  console.debug(getValues());
  console.debug(errors);

  const watchedAlreadyRegistered = watch("alreadyRegistered");

  const onChangeIbanSubmit = (formValues: IBANData) => {
    changeIBAN(currentContract!.name, formValues);
  }

  const handleClose = () => {
    onClose();
  };

  const renderInitForm = () => {
    const sending = IBANsState === "submitting";
    const loading = IBANsState === "fetching";

    const ibanChoices = ibans.filter((bank) => bank.id !== (currentContract?.bank_account?.id ?? 0)).map((bank) => {
      const label = <div>{bank.printable_iban}</div>;
      return <FormControlLabel key={bank.id} value={bank.id} control={<Radio />} label={label} disabled={sending} />;
    });

    return <>
      <form onSubmit={handleSubmit(onChangeIbanSubmit)}>

        <DialogContent>

          <Controller
            name="alreadyRegistered"
            control={control}
            render={({ field }) => (
              <FormControl style={{ width: "100%" }}>
                <InputLabel shrink>
                  {t('common:text.iban_change_to_use')}
                </InputLabel>
                <Select
                  {...field}
                >
                  <MenuItem key="yes" value="yes">
                    {t('common:text.iban_change_existent')}
                  </MenuItem>
                  <MenuItem key="no" value="no">
                    {t('common:text.iban_change_new')}
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {watchedAlreadyRegistered === 'no' && <>

            <Controller
              name="iban"
              control={control}
              render={({ field }) => (
                <FormControl style={{ width: "100%" }}>
                  <TextField 
                    label={t('common:text.iban_field_label')}
                    placeholder={t('common:text.iban_field_placeholder')}
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
              )}
            />
            {newIBANErrors?.iban &&
              <FormHelperText error={true}>
                {newIBANErrors.iban.message}
              </FormHelperText>
            }

            <Controller
              name="isOwner"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value ?? false}
                      onChange={field.onChange}
                    />
                  }
                  label={t('common:text.iban_change_confirmation')}
                />
              )}
            />
            {newIBANErrors?.isOwner &&
              <FormHelperText error={true}>
                {newIBANErrors.isOwner.message}
              </FormHelperText>
            }
          </>}

          {watchedAlreadyRegistered === 'yes' && <>
            {!(IBANsState === "fetching") ?
              ibanChoices.length ?
                <>
                  <Controller
                    name="bankId"
                    control={control}
                    render={({ field }) =>(
                      <RadioGroup
                        {...field}
                        value={field.value ?? null}
                        onChange={event => field.onChange(Number(event.target.value))}
                      >
                        {ibanChoices}
                      </RadioGroup>
                    )}
                  />
                  {existingIBANErrors?.bankId &&
                    <FormHelperText error={true}>
                      {existingIBANErrors.bankId.message}
                    </FormHelperText>
                  }
                </>
              :
                <p style={{ padding: '1vh' }}>
                  {t('common:text.iban_change_none')}
                </p>
              :
              <LoadingAnimation />
            }
          </>}

          {Settings.contract?.ibanChange?.showAddAttachmentsOnIbanChange &&
            <>
              <Controller
                name="files"
                control={control}
                render={({ field }) => (
                  <MultiUploadButton
                    onChange={field.onChange}
                    label={t('common:text.multiupload_change_iban_label')}
                    hint={t('common:text.multiupload_hint')}
                    anotherLabel={t('common:text.multiupload_change_iban_add_file')}
                    removeLabel={t('common:text.multiupload_remove_file')}
                    accept={'image/png, image/jpeg, image/*,.pdf'}
                  />
                )}
              />
              {existingIBANErrors.files &&
                <FormHelperText error={true}>
                  {existingIBANErrors.files.message}
                </FormHelperText>
              }
            </>
          }
        </DialogContent>

        <DialogActions sx={{display: "flex", justifyContent: "flex-start"}}>
          <Button
            variant={'outlined'}
            color={"primary"}
            onClick={handleClose}
            disabled={sending}
          >
            {t('common:text.power_modification_close')}
          </Button>
          <Button
            type="submit"
            variant={'contained'}
            color={'primary'}
            disabled={
              sending || loading ||
              (watchedAlreadyRegistered === "yes" && !ibanChoices.length)
            }
            loading={sending}
          >
            {t('common:text.iban_change_update')}
          </Button>
        </DialogActions>

      </form>
    </>
  };

  const renderSent = () => {
    return (
      <div>
        {waitingForSignature && <SignatureProcess />}
        {IBANsState === 'error' && <ErrorMessage />}
        <Button
          style={{ marginTop: '12px' }}
          color={'primary'}
          variant={'contained'}
          onClick={handleClose}
        >
          {t('common:text.iban_change_close')}
        </Button>
      </div>
    );
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="stretch"
    >
      <Grid item xs={6} style={{ padding: 12 }}>
        <Typography variant="h5">{t('common:text.iban_change_title')}</Typography>
        {IBANsState === 'fetching' && <LoadingAnimation />}
        {(IBANsState === 'uninitialized' || IBANsState === 'selecting' || IBANsState === 'submitting') && renderInitForm()}
        {(IBANsState === 'success' || IBANsState === 'error') && renderSent()}
      </Grid>
      <Grid item xs={6} style={{ padding: 12 }}>
        <Banner code='canvi_iban_ov' />
      </Grid>
    </Grid>
  );
};

export default ChangeIban;
